import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { aiService } from '../services/aiService';
import { ExtractedFuelData } from '../types/fuelConsumption';

export const useFuelReceiptAI = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const analyzeReceipt = async (imageBase64: string): Promise<ExtractedFuelData | null> => {
    setIsAnalyzing(true);
    setError(null);

    try {
      console.log('Starting receipt analysis...');
      const extractedData = await aiService.extractFuelDataFromReceipt(imageBase64);
      console.log('Raw AI response:', extractedData);
      
      if (!extractedData) {
        console.warn('No data extracted from receipt');
        setError(t('fuelReceipt.errors.noDataExtracted'));
        return null;
      }

      // Validate extracted data
      if (!extractedData.date && !extractedData.amount && !extractedData.volume) {
        console.warn('Insufficient data extracted:', extractedData);
        setError(t('fuelReceipt.errors.insufficientData'));
        return null;
      }

      console.log('Successfully extracted data:', extractedData);
      return extractedData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('fuelReceipt.errors.analysisError');
      setError(errorMessage);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const validateExtractedData = (data: ExtractedFuelData): boolean => {
    console.log('Validating extracted data:', data);

    if (!data.date || !data.amount || !data.volume) {
      console.warn('Missing required fields:', {
        hasDate: !!data.date,
        hasAmount: !!data.amount,
        hasVolume: !!data.volume
      });
      setError(t('fuelReceipt.errors.missingRequiredFields'));
      return false;
    }

    if (data.amount <= 0 || data.volume <= 0) {
      console.warn('Invalid values:', {
        amount: data.amount,
        volume: data.volume
      });
      setError(t('fuelReceipt.errors.invalidValues'));
      return false;
    }

    console.log('Data validation successful');
    return true;
  };

  return {
    analyzeReceipt,
    validateExtractedData,
    isAnalyzing,
    error,
    clearError: () => setError(null)
  };
};
